import { LayoutGrid, Grid, MessageCircle, Layout, LayoutGrid as GridIcon, ShoppingBag, Kanban, Target, Activity, TrendingUp, Briefcase, Zap, Users } from 'lucide-react';
import { config } from '../config';
import { isPartner } from '../utils/roleUtils';

interface BottomNavProps {
     activeTab: string;
     onTabChange: (tab: string) => void;
     hideFocus: boolean;
     user: { role?: string; email?: string } | null;
     userPath?: 'referral' | 'own-product' | null;
     pendingCount?: number;
 }

const BottomNav: React.FC<BottomNavProps> = ({ activeTab, onTabChange, hideFocus, user, userPath = null, pendingCount = 0 }) => {
     const role = (user?.role || 'CUSTOMER').toUpperCase();
     const isPartner = role.includes('PARTNER');

// Define the 7 Core Views
      const coreViews = [
        { id: 'dashboard', label: 'Dashboard', icon: Activity, roles: ['ADMIN', 'PARTNER', 'CLIENT', 'CUSTOMER'], paths: ['referral', 'own-product'] },
        { id: 'productivity', label: 'Prod', icon: Target, roles: ['ADMIN', 'PARTNER', 'CLIENT', 'CUSTOMER'], paths: ['referral', 'own-product'] },
        { id: 'cjms', label: 'Journey', icon: Kanban, roles: ['ADMIN', 'PARTNER'], paths: ['referral', 'own-product'] },
        { id: 'finance', label: 'Finance', icon: TrendingUp, roles: ['ADMIN', 'PARTNER'], paths: ['referral', 'own-product'] },
        { id: 'modules', label: 'Modules', icon: Grid, roles: ['ADMIN', 'PARTNER', 'CLIENT', 'CUSTOMER'], paths: ['referral', 'own-product'] },
        { id: 'portal', label: 'Portal', icon: Layout, roles: ['ADMIN', 'PARTNER', 'CLIENT', 'CUSTOMER'], paths: ['referral', 'own-product'] },
        { id: 'partner', label: 'Partner', icon: Users, roles: ['ADMIN', 'PARTNER'], paths: ['referral'] }, // Primarily for referral path
        { id: 'sovereign_hub', label: 'Sovereign', icon: Zap, roles: ['ADMIN', 'PARTNER'], paths: ['referral', 'own-product'] },
        { id: 'files', label: 'Files', icon: Briefcase, roles: ['ADMIN', 'PARTNER', 'CLIENT', 'CUSTOMER'], paths: ['referral', 'own-product'] },
        { id: 'landing', label: 'Offer', icon: LayoutGrid, roles: ['ADMIN', 'PARTNER', 'CLIENT', 'CUSTOMER'], paths: ['referral', 'own-product'] }
      ];

return (
     <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[95%] max-w-lg bg-white/80 backdrop-blur-2xl border border-white/20 shadow-2xl rounded-[2.5rem] px-2 py-2 z-40">
         <div className="flex justify-around items-center">
             {coreViews.map(view => {
                 // Check role-based access
                 const hasRole = view.roles.includes(role) || (view.roles.includes('PARTNER') && isPartner);
                 
                 // Check path-based access
                 let hasPath = true; // Default to true (no restriction)
                 if (view.paths && view.paths.length > 0) {
                     // If the view has path restrictions, check if user's path is allowed
                     if (userPath !== null) {
                         // User has a path set - check if it's allowed for this view
                         hasPath = view.paths.includes(userPath);
                     } else {
                         // User has no path set - show all path-restricted views by default
                         // (This prevents hiding everything when path isn't set yet)
                         hasPath = true;
                     }
                 }
                 
                 const hasAccess = hasRole && hasPath;
                 
                 if (!hasAccess) {return null;}

                 const isSovereign = view.id === 'sovereign_hub';
                 const hasBadge = isSovereign && pendingCount > 0;

                    return (
                        <button
                            key={view.id}
                            onClick={() => onTabChange(view.id)}
                            className={`flex flex-col items-center justify-center py-3 rounded-full transition-all duration-300 w-full relative ${activeTab === view.id ? 'bg-blue-700 text-white shadow-lg shadow-blue-200' : 'text-slate-400'}`}
                        >
                            <div className="relative">
                                <view.icon size={20} strokeWidth={2.5} />
                                {hasBadge && (
                                    <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center bg-red-500 text-white text-[7px] font-black rounded-full border border-white px-1 animate-pulse">
                                        {pendingCount > 9 ? '9+' : pendingCount}
                                    </span>
                                )}
                            </div>
                            <span className="text-[10px] font-black mt-1 uppercase tracking-tight">{view.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default BottomNav;
